import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, Eye, Download, RotateCcw, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const DISCREPANCY_LABEL = {
  internal_paid_yokke_not_success: "Internal PAID, Yokke belum sukses",
  yokke_success_internal_not_paid: "Yokke SUKSES, Internal belum PAID",
  internal_refunded_but_yokke_still_success: "Internal REFUNDED, Yokke masih sukses",
  other_mismatch: "Mismatch lain",
};

export default function Reconciliation() {
  const { hasFeature } = useAuth();
  const canRefund = hasFeature("refunds");
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingRow, setRefreshingRow] = useState("");
  const [showOnly, setShowOnly] = useState("all"); // all | mismatch | match
  const [logTarget, setLogTarget] = useState(null);
  const [logs, setLogs] = useState([]);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundPin, setRefundPin] = useState("");
  const [refundReason, setRefundReason] = useState("Pembayaran terlambat setelah kasir menutup QRIS");
  const [refunding, setRefunding] = useState(false);

  useEffect(() => { api.get("/outlets").then((r) => setOutlets(r.data || [])); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const params = () => ({
    outlet_id: outletId || undefined,
    start: start ? start + "T00:00:00" : undefined,
    end: end ? end + "T23:59:59" : undefined,
  });

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get("/reports/reconciliation", {
        params: { ...params(), refresh, refresh_scope: "mismatch" },
      });
      setData(r.data);
      if (refresh) {
        const count = r.data?.summary?.refreshed || 0;
        const failed = r.data?.summary?.refresh_failed || 0;
        if (failed > 0) {
          toast.warning(`${count} berhasil, ${failed} gagal di-inquiry dari Yokke`);
        } else {
          toast.success(count > 0
            ? `${count} record selisih berhasil di-inquiry dari Yokke`
            : "Tidak ada record selisih yang perlu di-inquiry");
        }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const refreshSingleRow = async (row) => {
    if (!row?.sale_id || !row?.can_refresh) return;
    setRefreshingRow(row.sale_id);
    try {
      const r = await api.get("/reports/reconciliation", {
        params: {
          sale_id: row.sale_id,
          refresh: true,
          refresh_scope: "all",
        },
      });
      const refreshed = r.data?.summary?.refreshed || 0;
      const failed = r.data?.summary?.refresh_failed || 0;
      if (refreshed > 0) {
        toast.success(`Invoice ${row.invoice_no} berhasil di-inquiry dari Yokke`);
      } else if (failed > 0) {
        toast.error(`Inquiry Yokke untuk invoice ${row.invoice_no} gagal`);
      } else {
        toast.warning(`Invoice ${row.invoice_no} tidak memiliki data referensi untuk inquiry`);
      }
      await load(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inquiry single row gagal");
    } finally {
      setRefreshingRow("");
    }
  };

  const openLogs = async (sale_id) => {
    setLogTarget(sale_id);
    try {
      const r = await api.get("/yokke-logs", { params: { sale_id } });
      setLogs(r.data || []);
    } catch { setLogs([]); }
  };

  const openLateRefund = (row) => {
    if (!canRefund) return;
    setRefundTarget(row);
    setRefundPin("");
    setRefundReason("Pembayaran terlambat setelah kasir menutup QRIS");
  };

  const submitLateRefund = async () => {
    if (!refundTarget) return;
    if (!refundPin || refundPin.length < 4) {
      toast.error("PIN supervisor minimal 4 digit");
      return;
    }
    setRefunding(true);
    try {
      const r = await api.post(`/sales/${refundTarget.sale_id}/refund`, {
        supervisor_pin: refundPin,
        reason: refundReason,
      });
      toast.success(`QRIS berhasil di-refund oleh ${r.data?.approver || "supervisor"}`);
      setRefundTarget(null);
      await load(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Refund QRIS gagal");
    } finally {
      setRefunding(false);
    }
  };

  const exportCSV = () => {
    if (!data?.rows?.length) { toast.error("Tidak ada data"); return; }
    const headers = ["invoice_no","created_at","outlet","cashier","internal_amount","internal_status","yokke_amount","yokke_status","reference_no","approval_code","match","discrepancy"];
    const outletMap = Object.fromEntries(outlets.map(o => [o.id, o.name]));
    const lines = [headers.join(",")].concat(data.rows.map((r) => [
      r.invoice_no, r.created_at, outletMap[r.outlet_id]||"", r.cashier_name||"",
      r.internal_amount, r.internal_status, r.yokke_amount, r.yokke_status,
      r.reference_no||"", r.approval_code||"", r.match, r.discrepancy||""
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `rekonsiliasi_${start}_${end}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const filteredRows = (data?.rows || []).filter((r) => {
    if (showOnly === "mismatch") return !r.match;
    if (showOnly === "match") return r.match;
    return true;
  });

  const summary = data?.summary;
  const mismatchTotal = (summary?.internal_paid_yokke_not_success || 0) + (summary?.yokke_success_internal_not_paid || 0) + (summary?.internal_refunded_yokke_not_cancelled || 0);
  const outletMap = Object.fromEntries(outlets.map(o => [o.id, o.name]));

  return (
    <div className="p-6 lg:p-10">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">RECONCILIATION</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Rekonsiliasi QRIS Harian</h1>
          <p className="mt-1 text-sm text-zinc-500 max-w-2xl">Bandingkan transaksi internal (Sales) dengan status Yokke (qr-mpm-query). Pastikan tidak ada selisih sebelum closing kasir.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportCSV} data-testid="recon-export-btn" className="px-4 py-2.5 text-sm border border-border hover:bg-zinc-100 inline-flex items-center gap-1"><Download className="h-3.5 w-3.5"/>Export CSV</button>
          <button onClick={() => load(true)} disabled={refreshing} data-testid="recon-refresh-btn" className="px-4 py-2.5 text-sm bg-[#002FA7] text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={"h-4 w-4 " + (refreshing ? "animate-spin" : "")}/>
            {refreshing ? "Inquiry Selisih…" : "Refresh Selisih dari Yokke"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select data-testid="recon-outlet" value={outletId} onChange={(e)=>setOutletId(e.target.value)} className="border border-border px-3 py-2 text-sm">
          <option value="">Semua Outlet</option>{outlets.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <input data-testid="recon-start" type="date" value={start} onChange={(e)=>setStart(e.target.value)} className="border border-border px-3 py-2 text-sm"/>
        <input data-testid="recon-end" type="date" value={end} onChange={(e)=>setEnd(e.target.value)} className="border border-border px-3 py-2 text-sm"/>
        <button onClick={()=>load(false)} className="px-4 py-2 text-sm border border-zinc-900 hover:bg-zinc-900 hover:text-white">Terapkan</button>
        <div className="ml-auto inline-flex border border-border">
          {[
            ["all","Semua"],["mismatch","Selisih"],["match","Match"],
          ].map(([k, l]) => (
            <button key={k} onClick={()=>setShowOnly(k)} className={"px-4 py-2 text-xs uppercase tracking-wider font-semibold " + (showOnly===k?"bg-zinc-900 text-white":"")}>{l}</button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 grid-borders mb-8">
        {[
          { l: "Total Transaksi", v: summary?.total ?? 0, raw: true },
          { l: "Match", v: summary?.matched ?? 0, raw: true, color: "text-emerald-700" },
          { l: "Selisih", v: mismatchTotal, raw: true, color: "text-rose-700" },
          { l: "Nominal Internal", v: formatIDR(summary?.amount_total_internal || 0) },
          { l: "Nominal Yokke", v: formatIDR(summary?.amount_total_yokke || 0) },
        ].map((s, i) => (
          <div key={i} className="p-5 bg-white">
            <div className="overline text-zinc-500">{s.l}</div>
            <div className={"mt-2 font-display font-black text-2xl lg:text-3xl tabular-nums " + (s.color||"")}>{s.raw ? s.v : s.v}</div>
          </div>
        ))}
      </div>

      {/* Discrepancy breakdown */}
      {mismatchTotal > 0 && (
        <div className="border border-rose-200 bg-rose-50 p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0"/>
          <div className="text-sm">
            <div className="font-semibold text-rose-800">Ditemukan {mismatchTotal} selisih:</div>
            <ul className="mt-1 text-rose-700 space-y-0.5">
              {summary.internal_paid_yokke_not_success > 0 && <li>· Internal PAID tapi Yokke belum sukses: <strong>{summary.internal_paid_yokke_not_success}</strong></li>}
              {summary.yokke_success_internal_not_paid > 0 && <li>· Yokke SUKSES tapi Internal belum PAID: <strong>{summary.yokke_success_internal_not_paid}</strong></li>}
              {summary.internal_refunded_yokke_not_cancelled > 0 && <li>· Internal REFUNDED tapi Yokke masih sukses: <strong>{summary.internal_refunded_yokke_not_cancelled}</strong></li>}
            </ul>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-border"><tr>
            <th className="text-left p-3 overline">Waktu</th>
            <th className="text-left p-3 overline">Invoice</th>
            <th className="text-left p-3 overline">Outlet</th>
            <th className="text-right p-3 overline">Internal</th>
            <th className="text-left p-3 overline">Status</th>
            <th className="text-right p-3 overline">Yokke</th>
            <th className="text-left p-3 overline">Yokke Status</th>
            <th className="text-left p-3 overline">Ref / Approval</th>
            <th className="text-left p-3 overline">Hasil</th>
            <th className="text-right p-3 overline">Aksi</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="p-8 text-center text-zinc-500">Memuat…</td></tr>}
            {!loading && filteredRows.length===0 && <tr><td colSpan={10} className="p-8 text-center text-zinc-500">Tidak ada transaksi Yokke pada rentang ini</td></tr>}
            {filteredRows.map((r) => (
              <tr key={r.sale_id} className={"border-b border-border " + (r.match ? "" : "bg-rose-50/40")} data-testid={`recon-row-${r.sale_id}`}>
                <td className="p-3 text-xs text-zinc-500 whitespace-nowrap">{formatDate(r.created_at)}</td>
                <td className="p-3 font-mono text-xs">{r.invoice_no}</td>
                <td className="p-3 text-xs">{outletMap[r.outlet_id] || "-"}</td>
                <td className="p-3 text-right tabular-nums">{formatIDR(r.internal_amount)}</td>
                <td className="p-3"><span className={"text-xs px-2 py-0.5 uppercase font-semibold " + (r.internal_status==="paid"?"bg-emerald-100 text-emerald-800":r.internal_status==="refunded"?"bg-rose-100 text-rose-700":"bg-amber-100 text-amber-800")}>{r.internal_status}</span></td>
                <td className="p-3 text-right tabular-nums">{r.yokke_amount ? formatIDR(r.yokke_amount) : <span className="text-zinc-400">-</span>}</td>
                <td className="p-3"><span className={"text-xs uppercase font-semibold " + (r.yokke_status==="success"?"text-emerald-700":r.yokke_status==="no_data"?"text-zinc-400":"text-rose-700")}>{r.yokke_status}</span></td>
                <td className="p-3 font-mono text-[10px] text-zinc-500">
                  {r.reference_no && <div>ref: {r.reference_no}</div>}
                  {r.approval_code && <div>apv: {r.approval_code}</div>}
                </td>
                <td className="p-3">
                  {r.match
                    ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5"/>MATCH</span>
                    : <span className="text-xs text-rose-700 font-semibold">{DISCREPANCY_LABEL[r.discrepancy] || "Selisih"}</span>}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center justify-end gap-3 whitespace-nowrap">
                    {r.can_refresh && (
                      <button
                        onClick={() => refreshSingleRow(r)}
                        disabled={refreshingRow === r.sale_id}
                        data-testid={`recon-refresh-${r.sale_id}`}
                        className="text-xs text-[#002FA7] font-semibold underline inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        <RefreshCw className={"h-3 w-3 " + (refreshingRow === r.sale_id ? "animate-spin" : "")}/>
                        {refreshingRow === r.sale_id ? "Inquiry…" : "Inquiry"}
                      </button>
                    )}
                    {canRefund && r.can_refund && (
                      <button
                        onClick={() => openLateRefund(r)}
                        data-testid={`recon-refund-${r.sale_id}`}
                        className="text-xs text-rose-700 font-semibold underline inline-flex items-center gap-1"
                      >
                        <RotateCcw className="h-3 w-3"/>Refund QRIS
                      </button>
                    )}
                    <button onClick={()=>openLogs(r.sale_id)} data-testid={`recon-logs-${r.sale_id}`} className="text-xs underline text-[#002FA7] inline-flex items-center gap-1"><Eye className="h-3 w-3"/>Logs</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={canRefund && !!refundTarget} onOpenChange={(o) => !o && !refunding && setRefundTarget(null)}>
        <DialogContent className="rounded-none border-border max-w-md" data-testid="recon-refund-dialog">
          <DialogHeader>
            <DialogTitle>Refund QRIS Terlambat</DialogTitle>
          </DialogHeader>
          {refundTarget && (
            <div className="space-y-4">
              <div className="border border-border p-4 bg-zinc-50">
                <div className="font-mono text-xs text-zinc-500">{refundTarget.invoice_no}</div>
                <div className="font-display font-black text-2xl tabular-nums mt-1">{formatIDR(refundTarget.yokke_amount || refundTarget.internal_amount)}</div>
                <div className="text-xs mt-2">
                  Internal: <strong className="uppercase text-amber-700">{refundTarget.internal_status}</strong>
                  <span className="mx-2">·</span>
                  Yokke: <strong className="uppercase text-emerald-700">{refundTarget.yokke_status}</strong>
                </div>
              </div>

              <div className="flex gap-2 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 p-3">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5"/>
                <div>
                  Sistem akan inquiry ulang ke Yokke, melakukan <code>qr-mpm-cancel</code>, lalu mengubah status internal langsung menjadi <strong>REFUNDED</strong>. Karena status internal sebelumnya PENDING, stok tidak ditambah kembali.
                </div>
              </div>

              <div>
                <label className="overline text-zinc-600 block mb-1.5">PIN Supervisor/Admin</label>
                <div className="relative">
                  <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
                  <input
                    data-testid="recon-refund-pin"
                    type="password"
                    value={refundPin}
                    onChange={(e) => setRefundPin(e.target.value)}
                    maxLength={8}
                    autoFocus
                    className="w-full border border-border pl-9 pr-3 py-3 text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    placeholder="••••"
                  />
                </div>
              </div>

              <div>
                <label className="overline text-zinc-600 block mb-1.5">Alasan Refund</label>
                <input
                  data-testid="recon-refund-reason"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setRefundTarget(null)} disabled={refunding} className="py-3 text-sm border border-border disabled:opacity-50">Batal</button>
                <button onClick={submitLateRefund} disabled={refunding} data-testid="recon-refund-submit" className="py-3 text-sm bg-rose-600 text-white font-semibold hover:bg-rose-700 disabled:opacity-50">
                  {refunding ? "Memproses…" : "Approve & Refund"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!logTarget} onOpenChange={(o) => !o && setLogTarget(null)}>
        <DialogContent className="rounded-none border-border max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="yokke-logs-dialog">
          <DialogHeader>
            <DialogTitle>Yokke API Logs · {logTarget?.slice(0,8)}…</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {logs.length === 0 && <div className="text-sm text-zinc-500">Tidak ada log untuk transaksi ini.</div>}
            {logs.map((l) => (
              <div key={l.id} className={"border p-3 " + (l.success ? "border-emerald-200" : "border-rose-200")}>
                <div className="flex items-center justify-between text-xs">
                  <div className="font-semibold uppercase tracking-wider">{l.operation}</div>
                  <div className="text-zinc-500 font-mono">{formatDate(l.created_at)} · {l.duration_ms}ms</div>
                </div>
                <div className="mt-1 text-xs">
                  <span className={"font-semibold " + (l.success?"text-emerald-700":"text-rose-700")}>{l.response_code || (l.error ? "ERROR" : "—")}</span>
                </div>
                {l.error && <div className="mt-2 text-xs text-rose-700 bg-rose-50 p-2 font-mono">{l.error}</div>}
                <details className="mt-2"><summary className="text-xs text-zinc-500 cursor-pointer">Request</summary>
                  <pre className="bg-zinc-50 p-2 mt-1 text-[10px] overflow-x-auto">{JSON.stringify(l.request, null, 2)}</pre>
                </details>
                <details className="mt-1"><summary className="text-xs text-zinc-500 cursor-pointer">Response</summary>
                  <pre className="bg-zinc-50 p-2 mt-1 text-[10px] overflow-x-auto">{JSON.stringify(l.response, null, 2)}</pre>
                </details>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
